"use client";

import { c as _c } from "react/compiler-runtime";
import { IconChevronDownOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { NodeIcon } from "@/components/ui/node-icon";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { cn } from "@/lib/utils";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { getRequiredConnectorType } from "@/utils/workflowTransform";
import { useState } from "react";
import { ResolvedDescriptionContent } from "./workflow-step-row/components/ResolvedDescriptionContent";

// Re-export everything from the refactored structure
export { ChainBadge, formatRRule, formatDelayTime, formatFilter, formatTokenAmount, MathOperationPayload, useChainInfo, useChainInfoMap, WorkflowStepRow } from "./workflow-step-row/index";
export type { ChainInfo, WorkflowBranch, WorkflowStep } from "./workflow-step-row/index";

// Import WorkflowStepRow for use in WorkflowStepsList
import type { WorkflowBranch } from "./workflow-step-row/index";
import { WorkflowStep, WorkflowStepRow } from "./workflow-step-row/index";
import { GenericPayload } from "./workflow-step-row/components/payloads/GenericPayload";
const branchColorClasses = {
  green: {
    label: "text-green-700 dark:text-green-300",
    line: "bg-green-300 dark:bg-green-700",
    dot: "bg-green-500 dark:bg-green-400"
  },
  red: {
    label: "text-red-700 dark:text-red-300",
    line: "bg-red-300 dark:bg-red-700",
    dot: "bg-red-500 dark:bg-red-400"
  },
  purple: {
    label: "text-purple-700 dark:text-purple-300",
    line: "bg-purple-300 dark:bg-purple-700",
    dot: "bg-purple-500 dark:bg-purple-400"
  },
  amber: {
    label: "text-amber-700 dark:text-amber-300",
    line: "bg-amber-300 dark:bg-amber-700",
    dot: "bg-amber-500 dark:bg-amber-400"
  }
} as const;

/**
 * BranchGroup - Renders collapsible branch sections connected to the timeline
 * Aligned to the 24px (w-6) dot column with line at left-[11px]
 */
function BranchGroup(t0) {
  const $ = _c(10);
  const {
    branches,
    linkToDetail,
    defaultExpanded: t1
  } = t0;
  const defaultExpanded = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="border-ds-neutral-300 absolute bottom-0 left-[11px] top-0 w-0 border-l-2 border-dashed dark:border-zinc-600" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== branches || $[2] !== defaultExpanded || $[3] !== linkToDetail) {
    let t4;
    if ($[5] !== defaultExpanded || $[6] !== linkToDetail) {
      t4 = (branch, idx) => <CollapsibleBranch key={idx} branch={branch} linkToDetail={linkToDetail} defaultExpanded={defaultExpanded} />;
      $[5] = defaultExpanded;
      $[6] = linkToDetail;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = branches.map(t4);
    $[1] = branches;
    $[2] = defaultExpanded;
    $[3] = linkToDetail;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <div className="relative">{t2}<div className="space-y-1 py-2">{t3}</div></div>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * CompactBranchStep - Renders a child step inside a branch with curved L-shaped connectors
 * Matches the RunTimelineIterationRow pattern from run detail view
 */
function CompactBranchStep(t0) {
  const $ = _c(57);
  const {
    step,
    isFirst,
    isLast,
    defaultExpanded: t1
  } = t0;
  const defaultExpanded = t1 === undefined ? true : t1;
  const isLogic = !!step.badge && step.badge === "Logic";
  const iconColorHex = isLogic ? "#a855f7" : step.isTrigger ? "#2450FF" : "#22c55e";
  const hasContent = !!step.description || step.payload && Object.keys(step.payload).length > 0;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const definition = useDefinitionByType(step.type);
  const iconVisual = useResolvedNodeVisual(step.type);
  const connectorTypes = useConnectorsStore(_temp);
  let t2;
  if ($[0] !== definition || $[1] !== step.type) {
    t2 = getRequiredConnectorType(step.type, definition);
    $[0] = definition;
    $[1] = step.type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const requiredConnectorType = t2;
  let t3;
  if ($[3] !== connectorTypes || $[4] !== requiredConnectorType) {
    t3 = requiredConnectorType ? connectorTypes.find(ct => ct.type === requiredConnectorType) : undefined;
    $[3] = connectorTypes;
    $[4] = requiredConnectorType;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const connectorTypeDefinition = t3;
  let t4;
  if ($[6] !== connectorTypeDefinition?.logoUrl || $[7] !== requiredConnectorType || $[8] !== step.connector) {
    t4 = requiredConnectorType ? {
      type: requiredConnectorType,
      logoUrl: connectorTypeDefinition?.logoUrl
    } : step.connector?.type ? {
      type: step.connector.type
    } : undefined;
    $[6] = connectorTypeDefinition?.logoUrl;
    $[7] = requiredConnectorType;
    $[8] = step.connector;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const connectorProp = t4;
  const t5 = hasContent && "cursor-pointer";
  let t6;
  if ($[10] !== t5) {
    t6 = cn("relative flex items-center gap-2 py-5", t5);
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== hasContent || $[13] !== isExpanded) {
    t7 = hasContent ? () => setIsExpanded(!isExpanded) : undefined;
    $[12] = hasContent;
    $[13] = isExpanded;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isFirst) {
    t8 = !isFirst && <div className="border-ds-neutral-300 absolute -top-10 bottom-10 left-0 w-0 border-l-2 border-dashed dark:border-zinc-600" />;
    $[15] = isFirst;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="rounded-bl-ds-sm border-ds-neutral-300 absolute left-0 top-1/2 z-[1] h-3 w-5 -translate-y-full border-b-2 border-l-2 dark:border-zinc-600" />;
    t10 = <div className="w-5 flex-shrink-0" />;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  const t11 = isLogic ? "bg-purple-100 dark:bg-purple-900/30" : step.isTrigger ? "bg-blue-100 dark:bg-blue-900/30" : "bg-green-100 dark:bg-green-900/30";
  let t12;
  if ($[19] !== t11) {
    t12 = cn("relative z-10 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full", t11);
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = isLogic ? "bg-purple-500" : step.isTrigger ? "bg-blue-500" : "bg-green-500";
  let t14;
  if ($[21] !== t13) {
    t14 = cn("h-2 w-2 rounded-full", t13);
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <div className={t14} />;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t15) {
    t16 = <div className={t12}>{t15}</div>;
    $[25] = t12;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = connectorProp ?? iconVisual.connector;
  let t18;
  if ($[28] !== iconColorHex || $[29] !== iconVisual.logoUrl || $[30] !== step.name || $[31] !== step.type || $[32] !== t17) {
    t18 = <NodeIcon id={step.type} subject={step.name} context="" logoUrl={iconVisual.logoUrl} connector={t17} className="h-6 w-6 flex-shrink-0 rounded-md" alt={step.name} color={iconColorHex} />;
    $[28] = iconColorHex;
    $[29] = iconVisual.logoUrl;
    $[30] = step.name;
    $[31] = step.type;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== step.name) {
    t19 = <span className="text-ds-text-secondary min-w-0 flex-1 truncate text-sm font-medium">{step.name}</span>;
    $[34] = step.name;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== hasContent || $[37] !== isExpanded) {
    t20 = hasContent && <span className="text-ds-text-disabled flex-shrink-0">{isExpanded ? <IconChevronDownOutline18 className="h-3 w-3" /> : <IconChevronRightOutline18 className="h-3 w-3" />}</span>;
    $[36] = hasContent;
    $[37] = isExpanded;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t16 || $[40] !== t18 || $[41] !== t19 || $[42] !== t20 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8) {
    t21 = <div className={t6} onClick={t7}>{t8}{t9}{t10}{t16}{t18}{t19}{t20}</div>;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== hasContent || $[48] !== isExpanded || $[49] !== isLast || $[50] !== step.connector || $[51] !== step.description || $[52] !== step.payload) {
    t22 = isExpanded && hasContent && <div className="flex"><div className="flex w-[1px] flex-shrink-0 flex-col">{!isLast && <div className="border-ds-neutral-300 relative -top-10 w-px flex-1 border-l-2 border-dashed dark:border-zinc-600" />}</div><div className="flex-1 space-y-2 pb-4 pl-12">{step.description && <div className="text-ds-text-subtle text-xs leading-relaxed"><ResolvedDescriptionContent text={step.description} payload={step.payload} /></div>}{step.payload && Object.keys(step.payload).length > 0 && <GenericPayload payload={step.payload} connector={step.connector} />}</div></div>;
    $[47] = hasContent;
    $[48] = isExpanded;
    $[49] = isLast;
    $[50] = step.connector;
    $[51] = step.description;
    $[52] = step.payload;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t21 || $[55] !== t22) {
    t23 = <div>{t21}{t22}</div>;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  return t23;
}
function _temp(state) {
  return state.connectorTypes;
}
function CollapsibleBranch(t0) {
  const $ = _c(35);
  const {
    branch,
    defaultExpanded: t1
  } = t0;
  const defaultExpanded = t1 === undefined ? true : t1;
  const [isOpen, setIsOpen] = useState(branch.defaultExpanded ?? false);
  const colors = branchColorClasses[branch.color];
  const stepCount = branch.steps.length;
  let t2;
  if ($[0] !== colors.line) {
    t2 = cn("absolute left-[13px] top-3 h-0.5 w-4", colors.line);
    $[0] = colors.line;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <div className={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== colors.dot) {
    t4 = cn("absolute left-[5px] top-1 z-10 h-3.5 w-3.5 rounded-full", colors.dot);
    $[4] = colors.dot;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className={t4} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isOpen) {
    t6 = () => setIsOpen(!isOpen);
    $[8] = isOpen;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== colors.label) {
    t7 = cn("flex items-center gap-1.5 py-1 text-sm font-semibold transition-colors", colors.label);
    $[10] = colors.label;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isOpen) {
    t8 = isOpen ? <IconChevronDownOutline18 className="h-3.5 w-3.5" /> : <IconChevronRightOutline18 className="h-3.5 w-3.5" />;
    $[12] = isOpen;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = stepCount !== 1 ? "s" : "";
  let t10;
  if ($[14] !== stepCount || $[15] !== t9) {
    t10 = <span className="text-ds-text-disabled dark:text-ds-text-muted font-normal">({stepCount} step{t9})</span>;
    $[14] = stepCount;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== branch.label || $[18] !== t10 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t11 = <button type="button" onClick={t6} className={t7}>{t8}{branch.label}{t10}</button>;
    $[17] = branch.label;
    $[18] = t10;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== branch.steps || $[24] !== defaultExpanded || $[25] !== isOpen || $[26] !== stepCount) {
    t12 = isOpen && <div className="pt-1">{stepCount > 0 ? <div>{branch.steps.map((step, idx) => <CompactBranchStep key={step.id} step={step} isFirst={idx === 0} isLast={idx === branch.steps.length - 1} defaultExpanded={defaultExpanded} />)}</div> : <p className="caption-xs pb-2">No steps in this branch</p>}</div>;
    $[23] = branch.steps;
    $[24] = defaultExpanded;
    $[25] = isOpen;
    $[26] = stepCount;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <div className="ml-10">{t11}{t12}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t3 || $[33] !== t5) {
    t14 = <div className="relative">{t3}{t5}{t13}</div>;
    $[31] = t13;
    $[32] = t3;
    $[33] = t5;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}

/**
 * WorkflowStepsList - Container for workflow steps
 *
 * Displays multiple workflow steps in order with connecting lines.
 * Supports branching: steps with `branches` render collapsible branch sections.
 */
interface WorkflowStepsListProps {
  steps: WorkflowStep[];
  className?: string;
  /**
   * Whether to link to the integration detail page
   * @default true
   */
  linkToDetail?: boolean;
  /**
   * Whether step content is expanded by default
   * @default true
   */
  defaultExpanded?: boolean;
}

/**
 * InlineBodySteps - Renders body steps for for-each nodes inline
 * without the collapsible "Body" header label.
 */
function InlineBodySteps(t0) {
  const $ = _c(15);
  const {
    steps,
    continueMainLine,
    defaultExpanded: t1
  } = t0;
  const defaultExpanded = t1 === undefined ? true : t1;
  if (steps.length === 0) {
    return null;
  }
  let t2;
  if ($[0] !== continueMainLine) {
    t2 = continueMainLine && <div className="border-ds-neutral-300 w-px flex-1 border-l-2 border-dashed dark:border-zinc-600" />;
    $[0] = continueMainLine;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="flex w-6 flex-shrink-0 flex-col items-center">{t2}</div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== defaultExpanded || $[5] !== steps) {
    let t5;
    if ($[7] !== defaultExpanded || $[8] !== steps.length) {
      t5 = (step, idx) => <CompactBranchStep key={step.id} step={step} isFirst={idx === 0} isLast={idx === steps.length - 1} defaultExpanded={defaultExpanded} />;
      $[7] = defaultExpanded;
      $[8] = steps.length;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = steps.map(t5);
    $[4] = defaultExpanded;
    $[5] = steps;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <div className="min-w-0 flex-1 py-3 pl-6">{t4}</div>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <div className="flex gap-3">{t3}{t5}</div>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export function WorkflowStepsList(t0) {
  const $ = _c(13);
  const {
    steps,
    className,
    linkToDetail: t1,
    defaultExpanded: t2
  } = t0;
  const linkToDetail = t1 === undefined ? true : t1;
  const defaultExpanded = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== className) {
    t3 = cn("space-y-0", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== defaultExpanded || $[3] !== linkToDetail || $[4] !== steps) {
    let t5;
    if ($[6] !== defaultExpanded || $[7] !== linkToDetail || $[8] !== steps.length) {
      t5 = (step, index) => {
        const isSingleBody = step.branches?.length === 1 && step.branches[0].label === "Body";
        const isLast = index === steps.length - 1;
        return <div key={step.id}><WorkflowStepRow step={step} index={index} isFirst={index === 0} isLast={isLast} totalSteps={steps.length} linkToDetail={linkToDetail} defaultExpanded={defaultExpanded} />{isSingleBody && step.branches[0].steps.length > 0 && <InlineBodySteps steps={step.branches[0].steps} continueMainLine={!isLast} defaultExpanded={defaultExpanded} />}{!isSingleBody && step.branches && step.branches.length > 0 && <BranchGroup branches={step.branches} linkToDetail={linkToDetail} defaultExpanded={defaultExpanded} />}</div>;
      };
      $[6] = defaultExpanded;
      $[7] = linkToDetail;
      $[8] = steps.length;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = steps.map(t5);
    $[2] = defaultExpanded;
    $[3] = linkToDetail;
    $[4] = steps;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}